import React, { useState } from 'react';
import styled, { css } from 'styled-components/macro';
import PropTypes from 'prop-types';
import { propsUtils } from 'src/utils';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  getBrandingColor, getBrandingStyleOverride, colorTypes, styleOverrides, getIsFontSizeIsHeader,
} from 'src/branding';
import { Block, TextElement } from './LayoutBase';

const GridItem = styled.div``;

const StyledGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(
    auto-fit,
    minmax(${({ width }) => width || 'auto'}, 1fr)
  );
  margin-right: -${({ gap }) => gap};

  ${GridItem} {
    margin-right: ${({ gap }) => gap};
  }
`;

export const Grid = ({
  children, width: defaultWidth, gap, ...props
}) => {
  const [width, setWidth] = useState(defaultWidth);

  const setColumnWidths = (columnRef) => {
    if (!columnRef || defaultWidth) {
      return;
    }

    for (let i = 0; i < columnRef.children.length; i += 1) {
      const { style } = columnRef.children[i];

      style.width = 'min-content';
      style.fontWeight = 'normal';
    }

    const maxWidth = Array.from(columnRef.children).reduce((maxWidth, elem) => {
      const elemWidth = elem.clientWidth;
      if (elemWidth > maxWidth) {
        return elemWidth;
      }
      return maxWidth;
    }, 0);

    for (let i = 0; i < columnRef.children.length; i += 1) {
      const { style } = columnRef.children[i];
      style.width = 'auto';
      style.fontWeight = null;
    }

    setWidth(`${maxWidth + parseFloat(gap)}px`);
  };

  return (
    <StyledGrid width={width} gap={gap} ref={setColumnWidths} {...props}>
      {children.map((child, key) => (
        <GridItem key={key}>{child}</GridItem>
      ))}
    </StyledGrid>
  );
};

Grid.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  gap: PropTypes.number,
};

Grid.defaultProps = {
  width: null,
  gap: 0,
};

export const LineSeparator = styled(Block)`
  flex: 0 0 auto;
  width: 100%;
  height: 1px;
  background: rgb(234, 234, 234);

  @media (min-width: 425px){
    ${({ desktopStyle }) => desktopStyle && desktopStyle};
  }

  @media (max-width: 425px){
    ${({ mobileStyle }) => mobileStyle && mobileStyle};
  }

`;

LineSeparator.defaultProps = {
  marginTop: 0,
  marginBottom: 0,
};

export const Cell = styled(Block)`
  position: relative;
  display: flex;
  flex-direction: column;
  flex: ${({ grow }) => (grow ? '1' : '0')}
    ${({ shrink }) => (shrink ? '1' : '0')};
  flex-basis: ${({ flexBasis }) => flexBasis};
  flex-wrap: ${({ wrap }) => (wrap ? 'wrap' : 'no-wrap')};
  border: ${({ border }) => border};
  border-radius: ${({ borderRadius }) => borderRadius};
  width: ${({ width }) => (width ? width : 'initial')};

  ${({ mediumCentered }) => (mediumCentered ? css`
    position: relative;
    left: 50%;
    width: 200px;
    transform: translateX(-50%);
  ` : '')}
`;

Cell.propTypes = {
  grow: PropTypes.bool,
  shrink: PropTypes.bool,
  wrap: PropTypes.bool,
  flexBasis: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  border: PropTypes.string,
  borderRadius: PropTypes.string,
};

Cell.defaultProps = {
  grow: false,
  noShrink: false,
  wrap: false,
  flexBasis: 'auto',
  border: null,
  borderRadius: null,
};

export const Column = styled(Cell)`
  .step-5-plus-checkboxes{

  @media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
    width: 950px;
  }
  }
`;

export const Row = styled(Column)`
  ${({ column, columnInMobile }) => !column
    && css`
      flex-direction: row;

      > ${LineSeparator} {
        width: 1px;
        height: 100%;
      }

      @media (max-width: 767px) {
        flex-direction: ${columnInMobile ? 'column' : 'row'};
      }
    `};

`;

const PageColumn = styled(Column)`
  ${getBrandingStyleOverride(styleOverrides.page)};
  @media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
    margin-bottom: 2em;
  }
`;
export const Page = ({
  title, paddingBottom = '7em', className, ...props
}) => {
  window.document.title = `${title} - Slakteweb`;

  const styledProps = { ...props, style: { ...props.style, paddingBottom } };
  return (
    <PageColumn
      overflowY="auto"
      className={className}
      overflowX="auto"
      grow
      shrink
      {...styledProps}
    />
  );
};

Page.propTypes = {
  noPadding: PropTypes.bool,
};

Page.defaultProps = {
  noPadding: false,
};

export const Section = styled(Column)`
  flex: 1 1 auto;
  margin: 0 auto;
  padding: 0 20px;
  width: 100%;
  max-width: 1200px;
  @media screen and (max-width: 650px) {
    padding: 0 10px;
  }
`;

export const ScreenReaderOnly = styled.a`
  overflow: hidden;
  height: 0;
  width: 0;

  :focus {
    height: auto;
    width: auto;
  }
`;

export const Label = ({ children, ...props }) => (
  <StyledLabel as="p" {...props}>
    {children}
  </StyledLabel>
);

const Bold = styled.span`
  font-weight: bold;
  ${getBrandingStyleOverride(styleOverrides.boldText)};
`;
export const InlineBold = ({ children, ...props }) => <Bold {...props}>{children}</Bold>;

export const StyledLabel = styled(TextElement)`
  vertical-align: middle;
  line-height: ${({ lineHeight }) => lineHeight};
  font-weight: ${({ bold, fontWeight }) => (bold ? 'bold' : fontWeight ? fontWeight : 'normal')};
  font-style: ${({ italic }) => (italic ? 'italic' : 'normal')};
  white-space: ${({ noWrap }) => (noWrap ? 'nowrap' : 'normal')};
  width: ${({ width }) => (width ? width : 'initial')};
  ${({ serif }) => (serif ? 'font-family: serif;' : '')}
  ${getBrandingStyleOverride(styleOverrides.text)};

  ${({ bold }) => (bold ? getBrandingStyleOverride(styleOverrides.boldText) : '')};
  ${(props) => (['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].indexOf(props.as) >= 0 ? getBrandingStyleOverride(styleOverrides.headerText) : '')};
  ${({ size, name }) => (size && getIsFontSizeIsHeader(size, name) ? getBrandingStyleOverride(styleOverrides.headerText) : '')}
`;

StyledLabel.propTypes = {
  lineHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  bold: PropTypes.bool,
  italic: PropTypes.bool,
  noWrap: PropTypes.bool,
};

Label.defaultProps = {
  lineHeight: '1.25',
  bold: false,
  italic: false,
  noWrap: false,
};

const StyledLinkLabel = styled(Label)`
  cursor: pointer;
  color: ${({ color }) => (color ? color : getBrandingColor(colorTypes.linkPrimary))};
  &,
  &:link,
  &:hover,
  &:focus {
    color: ${({ color }) => color};
  }
`;

const SmallLinkLabel = styled(StyledLinkLabel)`
  border-bottom: 1px solid ${getBrandingColor(colorTypes.linkPrimary)};
  text-decoration: none;
  width: fit-content;

  &:hover,
  &:focus {
    color: ${getBrandingColor(colorTypes.linkPrimaryHover)};
    text-decoration: none;
    border-bottom: 1.5px solid ${getBrandingColor(colorTypes.linkPrimaryHover)};
    box-sizing: border-box;
  }
`;

export const LinkLabel = ({
  target,
  href,
  title,
  onClick,
  children,
  small,
  to,
  ...props
}) => {
  const ChoosedLinkLabel = small ? SmallLinkLabel : StyledLinkLabel;

  return (
    (to && (
      <ChoosedLinkLabel
        as={Link}
        to={to}
        {...propsUtils.onClick(onClick)}
        {...props}
      >
        {children}
      </ChoosedLinkLabel>
    )) || (
      <ChoosedLinkLabel
        as="a"
        target={target}
        href={href}
        title={title}
        {...propsUtils.onClick(onClick)}
        {...props}
      >
        {children}
      </ChoosedLinkLabel>
    )
  );
};

LinkLabel.propTypes = {
  target: PropTypes.string,
  href: PropTypes.string,
  title: PropTypes.string,
  onClick: PropTypes.func,
  children: PropTypes.node.isRequired,
  textDecoration: PropTypes.string,
};

LinkLabel.defaultProps = {
  target: null,
  href: null,
  title: null,
  onClick: null,
};

export const Icon = styled(TextElement).attrs({ as: FontAwesomeIcon })`
  min-width: auto;
`;

Icon.propTypes = {
  size: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  color: PropTypes.string,
};

Icon.defaultProps = {
  size: '1em',
  color: 'inherit',
};

export const CircleIcon = styled(Icon)`
  width: 2em;
  line-height: 2em;
  display: inline-block;
  text-align: center;
  border-radius: 50%;
`;

const HTMLContent = styled.span`
  img {
    max-width: 100%;
    width: 100% !important;
    height: auto !important;
  }
`;

export const HTML = ({ children, style }) => (children
  ? React.Children.map(children, (child, key) => (
    <HTMLContent
      style={style}
      ref={(current) => {
        if (current) {
          const div = current;
          div.innerHTML = child;
        }
      }}
      key={key}
    />
  ))
  : null);
